import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type JSX } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  FileSearchIcon,
} from 'lucide-react';
import { AppDialog } from '@/components/ui/app-dialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  fetchGenerationEvidence,
  type GenerationCaseBasis,
  type GenerationSourceReceipt,
} from '@/lib/api/quality-generation-v4.js';

/** Actual retained prompt and tool deliveries; configuration alone never implies consumption. */
export function GenerationEvidence({
  runId,
  proposalId,
  version,
  sources = [],
  basis,
  compact = false,
}: {
  runId: string;
  proposalId: string;
  version: number;
  sources?: GenerationSourceReceipt[];
  basis?: GenerationCaseBasis[];
  compact?: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const evidenceTrigger = useRef<HTMLButtonElement | null>(null);
  const [sourceId, setSourceId] = useState<string>();
  const [offset, setOffset] = useState(0);
  const [previousOffsets, setPreviousOffsets] = useState<number[]>([]);
  const [promptOpen, setPromptOpen] = useState(false);
  const receiptSnapshot = JSON.stringify(sources);
  const [paginationReceiptSnapshot, setPaginationReceiptSnapshot] =
    useState(receiptSnapshot);
  const evidenceOffset =
    paginationReceiptSnapshot === receiptSnapshot ? offset : 0;
  useEffect(() => {
    setOffset(0);
    setPreviousOffsets([]);
    setPaginationReceiptSnapshot(receiptSnapshot);
  }, [receiptSnapshot]);
  const anchorIds = basis
    ? [...new Set(basis.flatMap((item) => item.evidenceIds))].sort()
    : undefined;
  const query = useQuery({
    queryKey: [
      'quality',
      runId,
      proposalId,
      version,
      'evidence',
      receiptSnapshot,
      sourceId,
      anchorIds,
      evidenceOffset,
    ],
    queryFn: () =>
      fetchGenerationEvidence(
        runId,
        proposalId,
        evidenceOffset,
        sourceId,
        anchorIds,
      ),
    enabled: open,
    networkMode: 'always',
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.state === 'collecting' ? 3000 : false,
  });
  function show(trigger: HTMLButtonElement, id?: string, prompt = false) {
    evidenceTrigger.current = trigger;
    setSourceId(id);
    setOffset(0);
    setPreviousOffsets([]);
    setPromptOpen(prompt);
    setOpen(true);
  }
  const visibleSources = (query.data?.sources ?? sources).filter(
    (source) =>
      (!sourceId || source.sourceId === sourceId) &&
      (!anchorIds ||
        source.anchors.some((anchor) => anchorIds.includes(anchor))),
  );
  const sourceIssueCount = sources.filter(
    (source) =>
      !isEmptyMemory(source) &&
      (isSourceError(source) ||
        source.collection === 'partial' ||
        ['not_forwarded', 'partial'].includes(source.consumption)),
  ).length;
  const sourceErrorCount = sources.filter(isSourceError).length;
  const explainedEmptySelection =
    !promptOpen &&
    visibleSources.length > 0 &&
    visibleSources.every(
      (source) => isSourceError(source) || isEmptyMemory(source),
    ) &&
    query.data?.entries.length === 0;
  return (
    <>
      {basis?.length ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 self-start px-1 text-xs font-normal text-muted-foreground hover:text-foreground"
          onClick={(event) => show(event.currentTarget)}
        >
          <FileSearchIcon className="size-3.5" aria-hidden />
          Sources & rationale
        </Button>
      ) : basis ? null : compact ? (
        <Button
          variant="outline"
          size="sm"
          onClick={(event) => show(event.currentTarget)}
        >
          Source data · {sources.length}
          {sourceErrorCount > 0 ? (
            <Badge variant="destructive">
              {sourceErrorCount} {sourceErrorCount === 1 ? 'issue' : 'issues'}
            </Badge>
          ) : null}
        </Button>
      ) : (
        <details aria-label="Generation sources" className="group pb-2 text-sm">
          <summary className="flex w-fit cursor-pointer list-none items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
            <ChevronDownIcon
              className="size-3 -rotate-90 transition-transform group-open:rotate-0"
              aria-hidden
            />
            <span>Source data · {sources.length}</span>
            {sourceIssueCount > 0 ? (
              <Badge variant="destructive">
                {sourceIssueCount} {sourceIssueCount === 1 ? 'issue' : 'issues'}
              </Badge>
            ) : null}
          </summary>
          <div className="mt-3 grid grid-cols-[minmax(0,1fr)_minmax(8rem,11rem)_1rem] gap-3 px-3 pb-2 text-xs text-muted-foreground">
            <span>Source</span>
            <span>Status</span>
          </div>
          <ul className="divide-y divide-border border-y border-border">
            {sources.map((source) => (
              <li key={source.sourceId}>
                <button
                  type="button"
                  aria-label={`${source.label} · ${collectionLabel(source)}`}
                  className="grid w-full grid-cols-[minmax(0,1fr)_minmax(8rem,11rem)_1rem] items-center gap-3 rounded-sm px-3 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={(event) =>
                    show(event.currentTarget, source.sourceId)
                  }
                >
                  <span className="min-w-0 break-words">{source.label}</span>
                  <span>
                    <Badge
                      variant={
                        isSourceError(source) ? 'destructive' : 'outline'
                      }
                    >
                      {collectionLabel(source)}
                    </Badge>
                  </span>
                  <ChevronRightIcon
                    className="size-4 text-muted-foreground"
                    aria-hidden
                  />
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-2 flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => show(event.currentTarget)}
            >
              View all source data
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(event) => show(event.currentTarget, undefined, true)}
            >
              View prompt
            </Button>
          </div>
        </details>
      )}
      <AppDialog
        open={open}
        onOpenChange={setOpen}
        title={
          basis
            ? 'Sources & rationale'
            : promptOpen
              ? 'Generator prompt'
              : 'Source data'
        }
        description={
          promptOpen
            ? 'The saved prompt for this version.'
            : 'Source content provided for these cases and any missing information.'
        }
        className="max-w-3xl"
        onOpenAutoFocus={() => undefined}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          evidenceTrigger.current?.focus();
        }}
      >
        {compact && !basis ? (
          <div className="mb-3 flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPromptOpen((previous) => !previous);
                setOffset(0);
                setPreviousOffsets([]);
              }}
            >
              {promptOpen ? 'Back to source data' : 'View prompt'}
            </Button>
          </div>
        ) : null}
        <div className="max-h-[70vh] space-y-4 overflow-y-auto text-sm">
          {basis ? (
            <p className="text-muted-foreground">
              Each card names a testcase field and explains why the generator
              included it from the provided sources. “Step 1 · Expected result”
              means the expected result of the first testcase step. Check the
              source documents and available excerpts below to assess that
              justification; it does not certify correctness.
            </p>
          ) : null}
          {basis?.map((item, index) => (
            <div key={index} className="rounded border border-border p-2">
              <strong>{basisFieldLabel(item.field)}</strong>
              <p>{item.explanation}</p>
              <details className="mt-2 text-muted-foreground">
                <summary className="cursor-pointer">Technical details</summary>
                <p>
                  {item.field} · {item.role}
                </p>
                <p>{item.evidenceIds.join(', ')}</p>
              </details>
              {item.evidenceIds.length === 0 ? (
                <p className="text-muted-foreground">
                  No source fragment recorded for this detail.
                </p>
              ) : null}
            </div>
          ))}
          {!promptOpen
            ? visibleSources.map((source) => (
                <div key={source.sourceId} className="space-y-3">
                  <div className="flex items-center gap-2">
                    <strong>{source.label}</strong>
                    {isSourceError(source) ? (
                      <Badge variant="destructive">Error</Badge>
                    ) : null}
                  </div>
                  {!isSourceError(source) &&
                  source.scope &&
                  source.scope !== source.label ? (
                    <p className="text-muted-foreground">{source.scope}</p>
                  ) : null}
                  {isSourceError(source) ? (
                    <div
                      role="alert"
                      className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive"
                    >
                      <CircleAlertIcon
                        className="mt-0.5 size-4 shrink-0"
                        aria-hidden
                      />
                      <div>
                        <p className="font-medium">{collectionLabel(source)}</p>
                        <p className="mt-1">
                          {sourceRecovery(source.collection)}
                        </p>
                      </div>
                    </div>
                  ) : isEmptyMemory(source) ? (
                    <p role="status" className="text-muted-foreground">
                      Only shared memory and personal entries marked safe to
                      share are used. Private personal entries are excluded.
                    </p>
                  ) : (
                    <>
                      <p>
                        {collectionLabel(source)} ·{' '}
                        {consumptionLabel(source.consumption)}
                      </p>
                      {source.reason ? <p>{sourceLimitation(source)}</p> : null}
                    </>
                  )}
                  {!isSourceError(source) &&
                  source.knownTotal !== null &&
                  source.receivedItems !== null &&
                  source.receivedItems < source.knownTotal ? (
                    <p>
                      Only {source.receivedItems} of {source.knownTotal}{' '}
                      selected items were available.
                    </p>
                  ) : null}
                  <details className="pt-1 text-xs text-muted-foreground">
                    <summary className="cursor-pointer">Source details</summary>
                    <p>
                      {collectionLabel(source)} ·{' '}
                      {consumptionLabel(source.consumption)}
                    </p>
                    <p>
                      Collected items: {source.receivedItems ?? 'unknown'} /{' '}
                      {source.knownTotal ?? 'unknown'}
                    </p>
                    {source.reason ? <p>{source.reason}</p> : null}
                    <p>{source.sourceId}</p>
                    {source.snapshotSha256 ? (
                      <p>{source.snapshotSha256}</p>
                    ) : null}
                  </details>
                </div>
              ))
            : null}
          {query.isLoading ? <p role="status">Loading actual data…</p> : null}
          {query.isError ? (
            <div role="alert">
              <p>{query.error.message}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void query.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : null}
          {query.data ? (
            <>
              {!promptOpen &&
              ((!sourceId && !anchorIds?.length) ||
                !query.data.receipt.available) &&
              ((!sourceId && !anchorIds?.length) ||
                !explainedEmptySelection ||
                !query.data.receipt.available ||
                query.data.receipt.omittedCount > 0 ||
                query.data.receipt.truncatedCount > 0) &&
              (!query.data.receipt.available ||
                !query.data.receipt.complete ||
                query.data.receipt.omittedCount > 0 ||
                query.data.receipt.truncatedCount > 0) ? (
                <p role="status">
                  Source record{' '}
                  {query.data.receipt.available
                    ? query.data.receipt.complete
                      ? 'complete'
                      : 'partial'
                    : 'unavailable'}
                  {!sourceId &&
                  !anchorIds?.length &&
                  query.data.receipt.omittedCount > 0
                    ? ` · ${query.data.receipt.omittedCount} omitted`
                    : ''}
                  {!sourceId &&
                  !anchorIds?.length &&
                  query.data.receipt.truncatedCount > 0
                    ? ` · ${query.data.receipt.truncatedCount} truncated`
                    : ''}
                </p>
              ) : null}
              {!basis && promptOpen ? (
                query.data.initialPrompt ? (
                  <>
                    <pre
                      id={`generation-prompt-${proposalId}`}
                      className="whitespace-pre-wrap break-words rounded bg-muted p-3"
                    >
                      {query.data.initialPrompt.text}
                    </pre>
                    {query.data.initialPrompt.truncated ? (
                      <p>Prompt preview truncated.</p>
                    ) : null}
                  </>
                ) : (
                  <div className="space-y-3">
                    <p>The prompt is currently unavailable.</p>
                    {!query.isError ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={query.isFetching}
                        onClick={() => void query.refetch()}
                      >
                        Try again
                      </Button>
                    ) : null}
                  </div>
                )
              ) : null}
              {!promptOpen
                ? query.data.entries.map((entry) => (
                    <article
                      key={entry.anchorId}
                      className="rounded border border-border p-3"
                    >
                      <strong>{entry.source.label}</strong>
                      <p className="text-muted-foreground">
                        {evidenceRoleLabel(entry.role)}
                      </p>
                      {entry.delivery !== 'delivered' ? (
                        <p role="status">
                          {deliveryLimitation(entry.delivery)}
                        </p>
                      ) : null}
                      <SourceContent result={entry.result} tool={entry.tool} />
                      {entry.resultTruncated || entry.inputTruncated ? (
                        <p>Fragment truncated in the retained evidence.</p>
                      ) : null}
                      <details className="mt-3 text-muted-foreground">
                        <summary className="cursor-pointer">
                          Technical details
                        </summary>
                        <p>
                          {entry.tool} · {entry.role} · {entry.delivery}
                        </p>
                        <p>{entry.anchorId}</p>
                        <pre className="whitespace-pre-wrap break-words">
                          {entry.actionInput}
                        </pre>
                        {entry.tool === 'search_workspace_knowledge' ||
                        entry.tool.endsWith('__search_workspace_knowledge') ? (
                          <pre className="whitespace-pre-wrap break-words">
                            {entry.result}
                          </pre>
                        ) : null}
                      </details>
                    </article>
                  ))
                : null}
              {!promptOpen &&
              !explainedEmptySelection &&
              query.data.entries.length === 0 ? (
                <p>No source content was retained for this selection.</p>
              ) : null}
              {!promptOpen &&
              (previousOffsets.length > 0 || query.data.nextOffset !== null) ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={previousOffsets.length === 0}
                    onClick={() => {
                      setOffset(previousOffsets.at(-1)!);
                      setPreviousOffsets(previousOffsets.slice(0, -1));
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={query.data.nextOffset === null}
                    onClick={() => {
                      setPreviousOffsets([...previousOffsets, offset]);
                      setOffset(query.data!.nextOffset!);
                    }}
                  >
                    Next fragments
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </AppDialog>
    </>
  );
}

function isEmptyMemory(source: GenerationSourceReceipt): boolean {
  return source.group === 'workspace_memory' && source.collection === 'empty';
}

function collectionLabel(source: GenerationSourceReceipt): string {
  if (isEmptyMemory(source)) return 'No memory available';
  const state = source.collection;
  const labels: Record<GenerationSourceReceipt['collection'], string> = {
    configured: 'Selected',
    pending: 'Waiting',
    partial: 'Partly received',
    ready: 'Available',
    empty: 'No readable content',
    unsupported: 'Unsupported',
    denied: 'Access denied',
    error: 'Could not collect',
  };
  return labels[state] ?? state;
}

function isSourceError(source: GenerationSourceReceipt): boolean {
  return (
    !isEmptyMemory(source) &&
    ['empty', 'unsupported', 'denied', 'error'].includes(source.collection)
  );
}

function sourceRecovery(collection: string): string {
  if (collection === 'empty')
    return 'Add requirements to this source or choose another one.';
  if (collection === 'denied')
    return 'Check access to this source and try again.';
  if (collection === 'unsupported')
    return 'Use a supported format or provide the requirements as text.';
  return 'Check the source and try again.';
}

function SourceContent({
  result,
  tool,
}: {
  result: string;
  tool: string;
}): JSX.Element {
  try {
    const payload: unknown =
      tool === 'search_workspace_knowledge' ||
      tool.endsWith('__search_workspace_knowledge')
        ? JSON.parse(result)
        : null;
    if (
      payload &&
      typeof payload === 'object' &&
      'evidence' in payload &&
      Array.isArray(payload.evidence) &&
      payload.evidence.every(
        (item: unknown) =>
          item &&
          typeof item === 'object' &&
          'excerpt' in item &&
          typeof item.excerpt === 'string',
      )
    ) {
      return (
        <div className="mt-2 space-y-3 text-sm leading-relaxed">
          {payload.evidence.length === 0 ? (
            <p>No matching source content was returned.</p>
          ) : null}
          {'status' in payload &&
          typeof payload.status === 'string' &&
          payload.status !== 'ok' ? (
            <p role="status">
              Source retrieval: {payload.status.replaceAll('_', ' ')}
            </p>
          ) : null}
          {payload.evidence.map((item, index) => (
            <div key={index}>
              {typeof item.title === 'string' ? (
                <strong>{item.title}</strong>
              ) : typeof item.filename === 'string' ? (
                <strong>{item.filename}</strong>
              ) : null}
              {typeof item.location === 'string' ? (
                <p className="text-muted-foreground">{item.location}</p>
              ) : null}
              <p className="whitespace-pre-wrap break-words">{item.excerpt}</p>
            </div>
          ))}
        </div>
      );
    }
  } catch {
    /** Plain source text remains unchanged. */
  }
  return (
    <div className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed">
      {result}
    </div>
  );
}

function deliveryLimitation(delivery: string): string {
  const labels: Record<string, string> = {
    failed:
      'The source request failed; this response could not provide evidence.',
    missing_response: 'No response was recorded for this source request.',
    non_text: 'The response contained no readable source text.',
    ambiguous:
      'The response could not be reliably matched to the source request.',
  };
  return Object.hasOwn(labels, delivery)
    ? labels[delivery]!
    : `Source response: ${delivery.replaceAll('_', ' ')}`;
}
function consumptionLabel(
  state: GenerationSourceReceipt['consumption'],
): string {
  const labels: Record<GenerationSourceReceipt['consumption'], string> = {
    pending: 'Waiting',
    forwarded: 'Provided to generator',
    partial: 'Only part provided to generator',
    not_forwarded: 'Not provided to generator',
    unknown: 'Generator input not confirmed',
  };
  return labels[state] ?? state;
}

function sourceLimitation(source: GenerationSourceReceipt): string {
  if (source.collection === 'empty')
    return 'No readable source content was available.';
  if (source.collection === 'denied')
    return 'Access to this source was denied.';
  if (source.collection === 'unsupported')
    return 'This source format could not be read.';
  if (source.collection === 'error')
    return 'The selected source could not be loaded.';
  return source.reason?.replaceAll('_', ' ') ?? '';
}

function evidenceRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    requirement: 'Requirement text',
    preparation: 'Preparation or test data',
    observation: 'Observed in the application',
    style: 'Existing test example',
    context: 'Background information',
    implementation: 'Application code',
    quality_rule: 'Quality rule',
    inference: 'Generator suggestion',
  };
  return Object.hasOwn(labels, role) ? labels[role]! : 'Source content';
}

function basisFieldLabel(field: string): string {
  if (field === 'precondition') return 'Preconditions';
  const preparationStep = field.match(
    /^preparation\.actions(?:\.(\d+)|\[(\d+)\])\.(content|expected)$/,
  );
  if (preparationStep)
    return `Preparation step ${Number(preparationStep[1] ?? preparationStep[2]) + 1} · ${preparationStep[3] === 'content' ? 'Action' : 'Expected result'}`;
  const step = field.match(
    /(?:test_steps_separated\[(\d+)\]|scenarioSteps\.(\d+))\.(content|expected)/,
  );
  if (step)
    return `Step ${Number(step[1] ?? step[2]) + 1} · ${step[3] === 'content' ? 'Action' : 'Expected result'}`;
  const labels: Record<string, string> = {
    'preparation.summary': 'Preparation',
    'preparation.role': 'Required role',
    'preparation.state': 'Starting state',
    'preparation.data': 'Test data',
    expected_result: 'Expected result',
    precondition: 'Preparation',
  };
  return Object.hasOwn(labels, field) ? labels[field]! : 'Case detail';
}
